<template>
  <div class="stat-grid-wrap">
    <div
      ref="track"
      class="stat-grid"
      @scroll.passive="onScroll"
      @touchstart.passive="pause"
      @mousedown="pause"
      @wheel.passive="pause"
    >
      <slot />
    </div>
    <div v-if="carousel && pages > 1" class="stat-grid__dots" role="tablist" aria-label="Страницы показателей">
      <button
        v-for="i in pages"
        :key="i"
        type="button"
        class="stat-grid__dot"
        :class="{ 'is-active': page === i - 1 }"
        :aria-label="'Показатели, страница ' + i"
        @click="go(i - 1, true)"
      ></button>
    </div>
  </div>
</template>

<script>
// Сетка карточек показателей. На компьютере — обычная адаптивная сетка.
// На телефоне — «листалка»: по четыре карточки (два столбца по две), страницы листаются пальцем
// и сами сменяются каждые несколько секунд (автопрокрутка останавливается, пока пользователь касается экрана).
const PER_PAGE = 4
const PHONE_QUERY = '(max-width: 640px)'

export default {
  name: 'StatGrid',
  props: {
    // период автопрокрутки, мс; 0 — выключить
    interval: { type: Number, default: 4500 }
  },
  data() {
    return {
      carousel: false,
      pages: 1,
      page: 0,
      paused: false
    }
  },
  mounted() {
    this.media = window.matchMedia(PHONE_QUERY)
    this.onMedia()
    if (this.media.addEventListener) this.media.addEventListener('change', this.onMedia)
    else if (this.media.addListener) this.media.addListener(this.onMedia)
    this.timer = setInterval(this.tick, this.interval || 4500)
    this.$nextTick(this.measure)
  },
  updated() {
    this.measure()
  },
  beforeDestroy() {
    clearInterval(this.timer)
    clearTimeout(this.resumeTimer)
    if (this.media && this.media.removeEventListener) this.media.removeEventListener('change', this.onMedia)
    else if (this.media && this.media.removeListener) this.media.removeListener(this.onMedia)
  },
  methods: {
    onMedia() {
      this.carousel = this.media.matches
      this.$nextTick(this.measure)
    },
    // Сколько страниц по четыре карточки (карточек может стать больше, когда придут данные).
    measure() {
      const track = this.$refs.track
      if (!track) return
      this.pages = Math.max(1, Math.ceil(track.children.length / PER_PAGE))
      if (this.page >= this.pages) this.page = 0
    },
    onScroll() {
      const track = this.$refs.track
      if (!track || !this.carousel) return
      // Дошли до конца ленты: это последняя страница (её начало может быть дальше предела прокрутки).
      if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 3) {
        this.page = this.pages - 1
        return
      }
      let best = 0
      let bestDistance = Infinity
      for (let i = 0; i < this.pages; i++) {
        const first = track.children[i * PER_PAGE]
        if (!first) break
        const distance = Math.abs(first.offsetLeft - track.scrollLeft)
        if (distance < bestDistance) {
          best = i
          bestDistance = distance
        }
      }
      this.page = best
    },
    // Перейти на страницу; ручной переход (по точке) ставит автопрокрутку на паузу.
    go(index, manual) {
      const track = this.$refs.track
      const first = track && track.children[index * PER_PAGE]
      if (!first) return
      if (manual) this.pause()
      this.page = index
      const max = track.scrollWidth - track.clientWidth
      track.scrollTo({ left: Math.min(first.offsetLeft, max), behavior: this.reducedMotion() ? 'auto' : 'smooth' })
    },
    reducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    },
    tick() {
      if (!this.carousel || this.paused || this.pages < 2 || document.hidden || this.reducedMotion()) return
      this.go((this.page + 1) % this.pages)
    },
    // Пользователь листает сам: автопрокрутка возвращается через несколько секунд бездействия.
    pause() {
      this.paused = true
      clearTimeout(this.resumeTimer)
      this.resumeTimer = setTimeout(() => {
        this.paused = false
      }, 9000)
    }
  }
}
</script>

<style scoped>
.stat-grid-wrap { flex-shrink: 0; min-width: 0; }

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: var(--space-8);
}

.stat-grid__dots { display: none; }

/* Телефон: два столбца по две карточки — четыре на странице, страницы листаются вбок. */
@media (max-width: 640px) {
  .stat-grid {
    position: relative;
    grid-template-columns: none;
    grid-template-rows: repeat(2, 1fr);
    grid-auto-flow: column;
    grid-auto-columns: calc((100% - var(--space-5)) / 2);
    gap: var(--space-5);
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .stat-grid::-webkit-scrollbar { display: none; }
  /* Начало страницы — первый столбец каждой четвёрки */
  .stat-grid > :nth-child(4n + 1),
  .stat-grid > :nth-child(4n + 2) { scroll-snap-align: start; }

  .stat-grid__dots {
    display: flex;
    justify-content: center;
    gap: var(--space-5);
    padding-top: var(--space-5);
  }
  /* точка 10 px, зона нажатия 32 px: фон рисуется только внутри отступов */
  .stat-grid__dot {
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    padding: 11px;
    border: none;
    border-radius: 50%;
    background: var(--border-default);
    background-clip: content-box;
    cursor: pointer;
  }
  .stat-grid__dot.is-active { background: var(--primary); }
}
</style>
